
import React from 'react';
import './App.css';
import background from './batempalmabackground.png';
import Header from './components/Header';
import RoundOffersModal from './components/RoundOffersModal';
import useLocalStorage from './hooks/useLocalStorage';
import Ammounts from './components/Ammounts';
import RemovedAmountsModal from './components/RemovedAmountsModal';
import { useState } from 'react';

// All possible amounts (left + right)
const ALL_AMOUNTS = [
  0.1, 1, 5, 10, 20, 50, 75, 100, 250, 500, 700, 900,
  1000, 2500, 5000, 7500, 10000, 12500, 15000, 20000, 25000, 50000, 75000, 100000
];

// Excel-like estimate calculation
function calculateEstimate(remaining) {
  const n = remaining.length;
  if (n === 1) return remaining[0];

  const sum = remaining.reduce((a, b) => a + b, 0);
  const avg = sum / n;

  // Standard deviation
  const variance = remaining.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / n;
  const std = Math.sqrt(variance);

  // Volatility factor (0 = stable board, 1+ = very risky)
  const volatility = std / avg;

  // Round aggressiveness (tuned for Batem-Palma pacing)

  // More conservative round factors

    const roundFactor = (() => {
      if (n >= 18) return 0.18;
    if (n >= 14) return 0.233;
    if (n >= 11) return 0.322;
    if (n >= 8)  return 0.474;
    if (n >= 5)  return 0.57;
    if (n >= 3)  return 0.853;
    return 1.22; // final 2 boxes
  })();

  // Less severe volatility penalty for early rounds
    const riskPenalty = n >= 18 ? (1 - 0.22 * volatility) : (n >= 14 ? (1 - 0.18 * volatility) : (1 - 0.18 * volatility));
  const adjustedPenalty = Math.max(0.35, riskPenalty);

  // No boost
  const offer = avg * roundFactor * adjustedPenalty;

  return Math.round(offer);
}

const ROUND_BOXES = [6, 4, 3, 3, 3, 2, 1, 1];

function getRoundAndBoxesLeft(removedCount) {
  let round = 1;
  let boxesLeft = ROUND_BOXES[0];
  let total = 0;
  for (let i = 0; i < ROUND_BOXES.length; i++) {
    total += ROUND_BOXES[i];
    if (removedCount < total) {
      round = i + 1;
      boxesLeft = total - removedCount;
      break;
    }
  }
  if (removedCount >= total) {
    round = ROUND_BOXES.length + 1;
    boxesLeft = 0;
  }
  return { round, boxesLeft };
}

function App() {
  const [resetSignal, setResetSignal] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [removedLeft, setRemovedLeft] = useState([]);
  const [removedRight, setRemovedRight] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [offers, setOffers] = useLocalStorage('bp-offers', []);
  const [roundAmountsOrder, setRoundAmountsOrder] = useLocalStorage('bp-round-amounts', {});
  // Block further removals while modal is open
  const [blockRemovals, setBlockRemovals] = useState(false);
  const [removedModalOpen, setRemovedModalOpen] = useState(false);
  // Freeze round/boxesLeft until modal closes
  const [frozenRound, setFrozenRound] = useState(null);
  const [frozenBoxesLeft, setFrozenBoxesLeft] = useState(null);

  const handleReset = () => {
    setResetSignal((s) => s + 1);
    setRemovedLeft([]);
    setRemovedRight([]);
    setOffers([]);
    setRoundAmountsOrder({});
  };

  React.useEffect(() => {
    setFadeIn(false);
    const timer = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(timer);
  }, []);


  // Calculate round and boxes left
  const removedCount = removedLeft.length + removedRight.length;
  const { round, boxesLeft } = getRoundAndBoxesLeft(removedCount);
  // Use frozen values if set
  const displayRound = frozenRound !== null ? frozenRound : round;
  const displayBoxesLeft = frozenBoxesLeft !== null ? frozenBoxesLeft : boxesLeft;

  // Update estimate for current round
  const updateEstimate = (removedL, removedR, roundNum = round) => {
    const removed = [...removedL, ...removedR];
    const remaining = ALL_AMOUNTS.filter(v => !removed.includes(v));
    const estimate = calculateEstimate(remaining);
    setOffers(prev => {
      const next = [...prev];
      const idx = next.findIndex(r => r.round === roundNum);
      if (idx !== -1) {
        next[idx] = { ...next[idx], estimate };
      } else {
        next.push({ round: roundNum, estimate });
      }
      return next;
    });
  };

  // Pass callbacks to Ammounts to track removed
  const handleRemove = (side, value) => {
    if (blockRemovals) return;
    const prevRemoved = removedLeft.length + removedRight.length;
    const prevRoundInfo = getRoundAndBoxesLeft(prevRemoved);

    let newRemovedLeft = removedLeft;
    let newRemovedRight = removedRight;
    if (side === 'left') newRemovedLeft = [...removedLeft, value];
    else newRemovedRight = [...removedRight, value];

    setRemovedLeft(newRemovedLeft);
    setRemovedRight(newRemovedRight);
    updateEstimate(newRemovedLeft, newRemovedRight);

    const currentRound = String(prevRoundInfo.round);
    setRoundAmountsOrder(prev => ({
      ...prev,
      [currentRound]: [...(prev[currentRound] || []), value],
    }));

    // End of round detection
    const removedNow = prevRemoved + 1;
    const { boxesLeft: afterBoxesLeft, round: afterRound } = getRoundAndBoxesLeft(removedNow);
    if (afterBoxesLeft === 0 || afterRound !== prevRoundInfo.round) {
      setFrozenRound(prevRoundInfo.round);
      setFrozenBoxesLeft(prevRoundInfo.boxesLeft - 1); // -1 because we just removed one
      setBlockRemovals(true);
      setTimeout(() => setModalOpen(true), 350); // allow animation to finish
    }
  };


  // Modal open from header
  const handleHeaderOffersClick = () => {
    setModalOpen(true);
  };

  const handleProgressClick = () => {
    setRemovedModalOpen(true);
  };

  // Always show the current estimate based on remaining amounts
  const removed = [...removedLeft, ...removedRight];
  const remaining = ALL_AMOUNTS.filter(v => !removed.includes(v));
  const currentEstimate = calculateEstimate(remaining);

  // When modal closes, allow removals again
  const handleModalClose = () => {
    setModalOpen(false);
    setBlockRemovals(false);
    setFrozenRound(null);
    setFrozenBoxesLeft(null);
  };

  return (
    <div className="bp-background" style={{ backgroundImage: `url(${background})` }}>
      <div className={`bp-fade-in bp-header-fade${fadeIn ? ' bp-fade-in-active' : ''}`}>
        <Header round={displayRound} boxesLeft={displayBoxesLeft} estimate={currentEstimate} onOffersClick={handleHeaderOffersClick} onProgressClick={handleProgressClick} />
      </div>
      <div className="bp-container">
        <div className="bp-content">
          <Ammounts
            resetSignal={resetSignal}
            onRemove={handleRemove}
            onRestore={(side, value) => {
              let newRemovedLeft = removedLeft;
              let newRemovedRight = removedRight;
              if (side === 'left') newRemovedLeft = removedLeft.filter((v) => v !== value);
              else newRemovedRight = removedRight.filter((v) => v !== value);
              setRemovedLeft(newRemovedLeft);
              setRemovedRight(newRemovedRight);
              updateEstimate(newRemovedLeft, newRemovedRight);
              setRoundAmountsOrder(prev => {
                const next = {};
                for (const key of Object.keys(prev)) {
                  next[key] = prev[key].filter(v => v !== value);
                }
                return next;
              });
            }}
            removedLeft={removedLeft}
            removedRight={removedRight}
          />
        </div>
      </div>
      <footer className="bp-footer">
        <button className={`bp-reset-btn bp-fade-in${fadeIn ? ' bp-fade-in-active' : ''}`} onClick={handleReset}>Reset</button>
      </footer>
      <RemovedAmountsModal
        open={removedModalOpen}
        onClose={() => setRemovedModalOpen(false)}
        roundAmountsOrder={roundAmountsOrder}
      />
      <RoundOffersModal
        open={modalOpen}
        onClose={handleModalClose}
        offers={offers}
        setOffers={setOffers}
        round={round}
        resetSignal={resetSignal}
        roundAmountsOrder={roundAmountsOrder}
      />
    </div>
  );
}

export default App;
